<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useClassStore } from '../../stores/class';
import { useUserStore } from '../../stores';
import { ClassGroupInfo, ClassStudentInfo, ClassViewType } from '../../types/class';
import { utils } from '../../utils';
import { Session, sessionMap } from '../../types/session';
import Modal from '../General/Modal.vue';
import Heading from '../General/Heading.vue';
import { useI18n } from 'petite-vue-i18n';
import { TEXT } from '../../configs/text';
import ArrowDown from '../General/ArrowDown.vue';
import { toast } from 'vue3-toastify';

const user = useUserStore()
const classStore = useClassStore()

const modalShow = ref<boolean>(false)
const notiViewType = ref<ClassViewType>(ClassViewType.student)
const notiSession = ref<Session>(Object.values(Session)[0])
const newPoint = ref<number>(0)
const oldPoint = ref<number>(0)
const pointChanged = computed<number>(() => newPoint.value - oldPoint.value)

const student = computed<ClassStudentInfo | undefined>(() => classStore.selectedClassDetails?.students?.find(student => student.id === user.userInfo?.id))
const studentGroup = computed<ClassGroupInfo | undefined>(() => classStore.studentGroups.find(studentGroup => studentGroup.id === student.value?.group_id))

// TODO: optimize loops

watch(() => classStore.selectedClassDetails?.students, (newStudents, oldStudents) => {
    newStudents?.forEach((newStudent: ClassStudentInfo, index: number) => {
        if (!oldStudents?.length) return;

        const oldStudent = oldStudents[index]
        if (!newStudent.point || !oldStudent.point) return;

        for (const session in newStudent.point) {
            const currentPoint: number = newStudent.point[session as Session] || 0
            const prevPoint: number = oldStudent.point[session as Session] || 0
            if (currentPoint === prevPoint) continue;

            if (newStudent.id === user.userInfo?.id) {
                // current student noti
                notiSession.value = session as Session
                newPoint.value = currentPoint
                oldPoint.value = prevPoint
                notiViewType.value = ClassViewType.student
                modalShow.value = true
            } else {
                // other student noti
                const otherPointChange = currentPoint - prevPoint
                toast.info(t(TEXT.student_point_change_noti, {
                    point: otherPointChange > 0 ? `+${otherPointChange}` : otherPointChange,
                    student: newStudent.full_name
                }))
            }

            break;
        }
    })
})

watch(() => classStore.studentGroups, (newStudentGroups, oldStudentGroups) => {
    newStudentGroups?.forEach((newStudentGroup, index) => {
        if (!oldStudentGroups?.length) return;
        const oldStudentGroup = oldStudentGroups[index]

        const currentPoint = utils.calculateTotalPoint(newStudentGroup.point)
        const prevPoint = utils.calculateTotalPoint(oldStudentGroup.point)
        if (currentPoint === prevPoint) return;

        if (newStudentGroup.id === classStore.selectedClassDetails?.student_group_id) {
            // current student group noti
            newPoint.value = currentPoint
            oldPoint.value = prevPoint
            notiViewType.value = ClassViewType.group
            modalShow.value = true
        } else {
            // other student group noti
            const otherPointChange = currentPoint - prevPoint
            toast.info(t(TEXT.group_point_change_noti, {
                point: otherPointChange > 0 ? `+${otherPointChange}` : otherPointChange,
                group: newStudentGroup.name
            }))
        }
    })
})

const { t } = useI18n()
</script>

<template>
    <Modal v-model="modalShow" class="!z-40" :static="true">
        <div class="text-center">
            <Heading v-if="pointChanged > 0">{{ t(TEXT.congratulations_on_increasing_score) }}</Heading>
            <Heading v-if="pointChanged < 0">{{ t(TEXT.sorry_your_score_has_decreased) }}</Heading>
            <div v-if="notiViewType === ClassViewType.student" class="mt-5">
                {{ t(pointChanged > 0 ? TEXT.increase_reward_point_text : TEXT.decrease_reward_point_text, {
                    point: Math.abs(pointChanged),
                    session: t(sessionMap[notiSession])
                }) }}
            </div>
            <template v-if="notiViewType === ClassViewType.group">
                <div class="mt-5">
                    {{ t(pointChanged > 0 ? TEXT.increase_group_point_text : TEXT.decrease_group_point_text, {
                        point: Math.abs(pointChanged),
                    }) }}
                </div>
                <div class="font-bold text-xl">{{ studentGroup?.name }}</div>
            </template>
            <div class="my-6 flex items-center justify-center gap-4">
                <span>{{ t(TEXT.current_point) }}</span>
                <span class="font-bold text-3xl">{{ oldPoint }}</span>
                <ArrowDown class="-rotate-90" :color="pointChanged < 0 ? 'danger' : 'success'" />
                <span class="font-bold text-3xl" :class="{
                    'text-lime-500': pointChanged > 0,
                    'text-red-500': pointChanged < 0
                }">{{ newPoint }}</span>
            </div>
        </div>
    </Modal>
</template>